import classNames from 'classnames';
import React from 'react';
import Typography, {
  TypographyStyle,
} from '@/components/atoms/typography/typography';
import styles from './toggle-switch.module.scss';

type LabelPosition = 'left' | 'right';

export interface ToggleSwitchProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Indicates whether the field is checked */
  checked?: boolean;
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** Indicates whether the field has an error */
  error?: boolean;
  /** Expands the switch to the available width and separates label and switch */
  fullWidth?: boolean;
  /** String to uniquely identify the switch */
  id?: string;
  /** The text that is displayed next to the toggle */
  label?: string;
  /** The placement of the label relative to the switch */
  labelPosition?: LabelPosition;
  /** The typography style used for the label */
  labelTagStyle?: TypographyStyle;
  /** An event that is triggered on click */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const ToggleSwitch = ({
  checked,
  children,
  disabled,
  error,
  fullWidth,
  id,
  label,
  labelPosition = 'right',
  labelTagStyle = 'bodyMedium',
  ...rest
}: ToggleSwitchProps) => {
  return (
    <React.Fragment>
      <button
        {...rest}
        aria-checked={checked}
        aria-labelledby={id}
        className={classNames(styles.component, {
          [styles.isFullWidth]: fullWidth,
          [styles.isChecked]: checked,
          [styles.isDisabled]: disabled,
          [styles.hasError]: error,
          [styles.isLabelLeft]: labelPosition === 'left',
        })}
        role="switch"
        tabIndex={0}
      >
        <label className={styles.labelContainer} id={id}>
          <div aria-hidden="true" className={styles.handleWrapper}>
            <div className={styles.handle} />
          </div>

          {label && (
            <Typography
              className={styles.label}
              tag="span"
              tagStyle={labelTagStyle}
              text={label}
            />
          )}

          {children && <div className={styles.content}>{children}</div>}
        </label>
      </button>
    </React.Fragment>
  );
};

ToggleSwitch.displayName = 'ToggleSwitch';

export default ToggleSwitch;
